import { translateFn } from '../../../utils/translator.helper';
import { AdditionalData, TimelineItem } from '../../../ui/gzCollapsibleTimeline/types';

export function alertsToTimelineItems(alerts, contentRenderer, suffixRenderer, category) {
  return alerts.map(alert => {
    return alert.group
      ? alertToTimelineItem(alert.alerts[0], contentRenderer, suffixRenderer, category, alert.alerts.length, alert)
      : alertToTimelineItem(alert, contentRenderer, suffixRenderer, category);
  });
}

export function alertToTimelineItem(alert, contentRenderer, suffixRenderer, category, groupedEntriesCount?, alertGroup = null, additionalData?: AdditionalData): TimelineItem {
  return {
    id: category.toString() + " alert-id: " +  alert._id.toString(),
    label: alert.name,
    details: alertGroup ? renderDetails(alertGroup) : undefined,
    contentRenderer: () => contentRenderer(alert, additionalData, alertGroup),
    suffixRenderer: suffixRenderer ? () => suffixRenderer(alertGroup ?? alert) : undefined,
    groupedEntriesCount: groupedEntriesCount,
  };
}

function renderDetails(alertGroup): string {
  let subtitle = alertGroup.nodes.length > 1
    ? translateFn('Seen {NUMBER_OF_ALERTS} times on {NUMBER_OF_ENTITIES} entities', {
    NUMBER_OF_ALERTS: alertGroup.alerts.length,
    NUMBER_OF_ENTITIES: alertGroup.nodes.length
  })
  : translateFn('Seen {NUMBER_OF_ALERTS} times on 1 entity', {
      NUMBER_OF_ALERTS: alertGroup.alerts.length
    });

  if (alertGroup.interactions.length > 0) {
    subtitle = alertGroup.interactions.length > 1
    ? translateFn('Seen {NUMBER_OF_ALERTS} times in {NUMBER_OF_INTERACTIONS} interactions', {
      NUMBER_OF_ALERTS: alertGroup.alerts.length,
      NUMBER_OF_INTERACTIONS: alertGroup.interactions.length
    })
    : translateFn('Seen {NUMBER_OF_ALERTS} times in 1 interaction', {
        NUMBER_OF_ALERTS: alertGroup.alerts.length
      });
  }

  return subtitle;
}

export function mapAlertsByDate(alerts) {
  const translateDate = (value) => {
    const date = new Date(value);
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-GB', options).replace(/ /g, ' ');
  };

  return groupAlertsByFieldAndSortByDate(alerts, 'lastUpdated', translateDate);
}

export function mapAlertsByKillchain(alerts, translateKillChainPhases) {
  return groupAlertsByFieldAndSortByDate(alerts, 'tactic', translateKillChainPhases);
}

export function mapAlertsBySeverity(alerts, translateEdrEventSeverity) {
  return groupAlertsByFieldAndSortByDate(alerts, 'severity', translateEdrEventSeverity);
}

export function mapAlertsBySensors(alerts, translateXdrSensors) {
  return groupAlertsByFieldFromArrayAndSortByDate(alerts, 'sensors', translateXdrSensors);
}

export function mapAlertsByNodes(alerts) {
  const groupedAlerts = alerts.reduce((result, alert) => {
    const nodeDetails: Record<string, Object[]> = alert.nodesDetails;
    for (const [_, nodes] of Object.entries(nodeDetails)) {
      nodes.forEach(node => groupByFieldCallback(result, alert, node['name']));
    }
    return result;
  }, {});

  return sortGroupedAlertsByDate(groupedAlerts);
}

export function mapAlertsByAlertName(alerts) {
  const alertsWithoutGroup = alerts.filter(alert => !alert.group);
  const groupedAlerts = groupAlertsByField(alertsWithoutGroup, 'name');
  return sortGroupedAlerts(groupedAlerts, (a, b) => b.severity - a.severity);
}

export function mapAlertsByIncidentNumber(alerts) {
  // alert.incidentNumber is incident number of the XDR Incident, which should be translated to #X - Organization
  // alert.incidents[].number is incident number of EDR incident, which should be translated to #Y - Endpoint
  const translateIncidentNumber = (number, type: 'XDR' | 'EDR') => {
    if (type === 'XDR') {
      return translateFn('#{number} - Organization', { number: number });
    }
    return translateFn('#{number} - Endpoint', { number: number });
  }

  interface IncidentNumberGroups {
    xdrGroup: {},
    edrGroup: {}
  }

  const translateXdrNumber = (number) => translateIncidentNumber(number, 'XDR');
  const translateEdrNumber = (number) => translateIncidentNumber(number, 'EDR');

  const incidentsNumberCallback = (result: IncidentNumberGroups, alert) => {
    // group by XDR Incident Number
    if (alert['incidentNumber']) {
      groupByFieldCallback(result.xdrGroup, alert, alert['incidentNumber'], translateXdrNumber);
    }
    // group by EDR Incident Number
    if (alert['incidents']) {
      alert['incidents'].forEach(incident => {
        groupByFieldCallback(result.edrGroup, alert, incident['number'], translateEdrNumber);
      });
    }

    // Alert Group, group by xdr incident number and edr incidents number
    if (alert['alerts']) {
      alert['alerts'].forEach(alertFromGroup => {
        groupByFieldCallback(result.xdrGroup, alert, alertFromGroup['incidentNumber'], translateXdrNumber);
        if (alertFromGroup['incidents']) {
          alertFromGroup['incidents'].forEach(incident => {
            groupByFieldCallback(result.edrGroup, alert, incident['number'], translateEdrNumber);
          });
        }
      });
    }
  };

  const groupedAlerts: IncidentNumberGroups = alerts.reduce((result: IncidentNumberGroups, alert) => {
    incidentsNumberCallback(result, alert);
    return result;
  }, { xdrGroup: {}, edrGroup: {} });

  const orderedGroupedAlerts = {
    ...groupedAlerts.xdrGroup,
    ...groupedAlerts.edrGroup
  };

  return sortGroupedAlertsByDate(orderedGroupedAlerts);
}

function groupAlertsByFieldFromArrayAndSortByDate(alerts: Array<{}>, field: string, translatorForKey?: Function) {
  const groupedAlerts = alerts.reduce((result, alert) => {
    for (const key of alert[field]) {
      groupByFieldCallback(result, alert, key, translatorForKey);
    }
    return result;
  }, {});

  return sortGroupedAlertsByDate(groupedAlerts);
}

function groupAlertsByField(alerts: Array<{}>, field: string, translatorForKey?: Function) {
  return alerts.reduce((result, alert) => {
    groupByFieldCallback(result, alert, alert?.[field], translatorForKey);
    return result;
  }, {});
}

function groupAlertsByFieldAndSortByDate(alerts: Array<{}>, field: string, translatorForKey?: Function) {
  const groupedAlerts = groupAlertsByField(alerts, field, translatorForKey);

  return sortGroupedAlertsByDate(groupedAlerts);
}

function groupByFieldCallback(result: {}, alert: {}, key: any, translatorForKey?: Function) {
  if (!key) {
    return;
  }

  const formattedKey = !translatorForKey
    ? key
    : translatorForKey(Number.isInteger(key) ? parseInt(key, 10) : key);

  if (!result[formattedKey]) {
    result[formattedKey] = {};
  }

  const alertId = alert['_id'] ?? (
    alert['alerts'] && alert['alerts'].length > 0
      ? alert['alerts'].reduce((idResult, alertFromGroup) => `${idResult}${alertFromGroup['_id']}`, '')
      : null
  );
  if (!alertId) {
    return;
  }

  if (!result[formattedKey][alertId]) {
    result[formattedKey][alertId] = alert;
  }
}

function sortGroupedAlertsByDate(groupedAlerts) {
  return sortGroupedAlerts(groupedAlerts, (a, b) => new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime())
}

function sortGroupedAlerts(groupedAlerts, comparator) {
  for (const key of Object.keys(groupedAlerts)) {
    groupedAlerts[key] = Object.values(groupedAlerts[key]);
    groupedAlerts[key].sort(comparator);
  }
  return groupedAlerts
}

export function getTimeRangeConfig(alerts) {
  const lastUpdatedDates = alerts.map(alert => new Date(alert.lastUpdated));
  const minDate = new Date(Math.min(...lastUpdatedDates));
  const maxDate = new Date(Math.max(...lastUpdatedDates));

  return {
    minTimestamp: minDate.getTime() / 1000,
    maxTimestamp: maxDate.getTime() / 1000,
  };
}
